import { useMemo } from 'react';
import { useNavigation } from '@/contexts/NavigationContext';
import { getNavIcon } from '@/lib/navigation-icons';
import { type LucideIcon } from 'lucide-react';

/** Stable navigationTag → local route map (immune to CMS link path changes) */
const tagToRouteMap: Record<string, string> = {
  // QuickLinks
  UnitSearch: '/units',
  PartsSearch: '#',
  DealerOrders: '/orders',
  PartsInvoice: '#',
  Reports: '#',
  Spiffs: '#',
  ClaimsAndPreAuths: '/dealer-claims',
  WarrantyRegistration: '/warranty-registration',
  ServiceCampaigns: '/service-campaigns',
  // MainMenu top-level & children
  ShopRvParts: '/parts',
  QuickOrder: '/quick-order',
  UnknownPartRequest: '/unknown-part-request',
  UnitSearch_Menu: '/units',
  InventoryTransfers: '/inventory-transfers',
  Rgas: '/rga-list',
  YourTickets: '/your-tickets',
  LaborOperationsLibrary: '/labor-operations',
  MyClaimedSpiffs: '/my-spiffs',
  DealerClaimedSpiffs: '/claimed-dealer-spiffs',
  SalesPersonSpiffs: '/salesperson-spiffs',
  DealerSpiffs: '/dealer-spiffs',
  DealerInvoices: '/dealer-invoices',
  MessageBoard: '/message-board',
  DigitalAssets: '/digital-assets',
  // ServiceMenu - My Profile / My Account sub-items
  SavedCarts: '/saved-carts',
  MyFavorites: '/my-favorites',
  MyContactInfo: '/contact-info',
  
  // ServiceMenu - My Dealership sub-items
  Dashboard: '/',
  MyDealershipInfo: '/dealership-info',
  DealerAddresses: '/addresses',
  ManageDealerUsers: '/dealer-users',
  UnitsOnLot: '/units?filter=inventory',
};

/** Legacy fallback: Map from DynamicWeb navigation link paths to local app routes */
const linkToRouteMap: Record<string, string> = {
  // QuickLinks paths
  '/en-us/vin-unit-search': '/units',
  '/en-us/parts-search': '#',
  '/en-us/dealer-orders': '/orders',
  '/en-us/parts-invoice': '#',
  '/en-us/spiffs': '#',
  '/en-us/claims-pre-auths': '/dealer-claims',
  '/en-us/warranty-registration': '/warranty-registration',
  '/en-us/service-campaigns': '/service-campaigns',
  // MainMenu paths
  '/en-us/parts/shop-rv-parts': '/parts',
  '/en-us/parts/quick-order': '/quick-order',
  '/en-us/parts/unknown-part-request': '/unknown-part-request',
  '/en-us/units/vin-unit-search': '/units',
  '/en-us/units/inventory-transfers': '/inventory-transfers',
  '/en-us/service/service-campaigns': '/service-campaigns',
  '/en-us/service/rgas': '/rga-list',
  '/en-us/service/claims-pre-auths': '/dealer-claims',
  '/en-us/service/your-tickets': '/your-tickets',
  '/en-us/service/labor-operations-library': '/labor-operations',
  '/en-us/sales/unit-warranty-registration': '/warranty-registration',
  '/en-us/sales/warranty-registration': '/warranty-registration',
  '/en-us/sales/my-claimed-spiffs': '/my-spiffs',
  '/en-us/sales/dealer-claimed-spiffs': '/claimed-dealer-spiffs',
  '/en-us/sales/sales-person-spiffs': '/salesperson-spiffs',
  '/en-us/sales/dealer-spiffs': '/dealer-spiffs',
  '/en-us/sales/dealer-invoices': '/dealer-invoices',
  '/en-us/message-board': '/message-board',
  '/en-us/message-board-articles': '/message-board',
  '/en-us/digital-assets': '/digital-assets',
  // ServiceMenu paths (legacy + renamed)
  '/en-us/my-account/saved-carts': '/saved-carts',
  '/en-us/my-account/my-favorites': '/my-favorites',
  '/en-us/my-account/my-contact-info': '/contact-info',
  
  '/en-us/my-profile/saved-carts': '/saved-carts',
  '/en-us/my-profile/my-favorites': '/my-favorites',
  '/en-us/my-profile/my-contact-info': '/contact-info',
  
  '/en-us/my-dealership/dashboard': '/',
  '/en-us/my-dealership/dealer-orders': '/orders',
  '/en-us/my-dealership/dealer-invoices': '/dealer-invoices',
  '/en-us/my-dealership/dealer-addresses': '/addresses',
  '/en-us/my-dealership/manage-dealer-users': '/dealer-users',
  '/en-us/my-dealership/units-on-lot': '/units?filter=inventory',
  '/en-us/my-dealership/my-dealership-info': '/dealership-info',
};

/** Resolve a navigation node to an internal route, preferring tag over link path */
export function resolveNodeRoute(node: { navigationTag?: string; link?: string; isClickable?: boolean }): string {
  if (node.navigationTag && tagToRouteMap[node.navigationTag]) {
    return tagToRouteMap[node.navigationTag];
  }
  if (node.isClickable === false) return '#';
  return resolveRoute(node.link);
}

/** Resolve a DynamicWeb link path to an internal route (legacy fallback) */
export function resolveRoute(link: string | undefined): string {
  if (!link) return '#';
  return linkToRouteMap[link] || '#';
}

/** Resolve a navigationTag to an icon */
export function resolveIcon(navigationTag: string | undefined): LucideIcon {
  return getNavIcon(navigationTag || '');
}

export interface NavigationNode {
  name: string;
  link: string;
  showInMenu: boolean;
  isClickable: boolean;
  navigationTag?: string;
  pageId?: number;
  nodes?: NavigationNode[];
}

export interface NavigationLink {
  title: string;
  url: string;
  icon: LucideIcon;
}

export interface NavigationGroup {
  title: string;
  type: 'dropdown' | 'link';
  url?: string;
  icon: LucideIcon;
  children: NavigationLink[];
}

interface UseNavigationApiParams {
  navigationId?: number;
  expandMode?: string;
  startLevel?: number;
  stopLevel?: number;
  includeFoldersAndHidden?: boolean;
  rootNavigationTag?: string;
}

/**
 * Hook that reads cached navigation data from NavigationContext.
 * Data is fetched once after login by NavigationProvider.
 */
export function useNavigationApi(params: UseNavigationApiParams) {
  const { quickLinksNodes, mainMenuNodes, serviceMenuNodes, isLoading, useMock } = useNavigation();

  const nodes = useMemo(() => {
    switch (params.rootNavigationTag) {
      case 'QuickLinks': return quickLinksNodes;
      case 'MainMenu': return mainMenuNodes;
      case 'ServiceMenu': return serviceMenuNodes;
      default: return null;
    }
  }, [params.rootNavigationTag, quickLinksNodes, mainMenuNodes, serviceMenuNodes]);

  return { nodes, isLoading, useMock };
}
